import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';
import Spinner from '../spinner/spinner';
import HeaderTo from './header-to';
import AttachmentCard from '../attachment/attachment-card';
import { selectFolder } from '../../actions/application';
import { clearSelectedMessage } from '../../services/application';
import { readMessageRaw } from '../../services/message-read';
import { downloadAttachmentAll, downloadWinmailDatFiles } from '../../services/message';
import { getSelectedFolder } from '../../selectors/folders';
import sanitize from '../../services/sanitize';
import ACTIONS from '../../actions/lexon';
import FavoriteIcon from '../../assets/icons/favorite.svg?react';
import TagIcon from '../../assets/icons/tag.svg?react';
import ClipIcon from '../../assets/icons/clip.svg?react';
import { setSelected, addMessageClassification } from '../../actions/messages';

export function addressGroups(address) {
  const ret = {
    name: '',
    email: ''
  };

  if (!address || typeof address !== 'string') {
    return ret;
  }

  // Extract name if quoted at the beginning: "Name"
  const quotedNameMatch = address.match(/^"(.*?)"/);
  if (quotedNameMatch) {
    ret.name = quotedNameMatch[1];
  } else {
    // Fallback name: strip any angle-bracket email to leave a possible display name
    ret.name = address.replace(/<[^>]*>/g, '').trim().replace(/^"|"$/g, '');
  }

  // Prefer email inside angle brackets if present
  const angleMatch = address.match(/<([^>]+)>/);
  if (angleMatch && angleMatch[1]) {
    ret.email = angleMatch[1].trim();
  } else {
    // Otherwise, try to find any email-like pattern in the string
    const emailMatch = address.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    if (emailMatch && emailMatch[0]) {
      ret.email = emailMatch[0].trim();
    } else {
      // Last resort: return the original element string as the email, as requested
      ret.email = address;
    }
  }

  return ret;
}

export class MessageViewer extends Component {
  constructor() {
    super();
    this.handleGetUserFromConnector =
      this.handleGetUserFromConnector.bind(this);
    this.toastObj = null;
    this.state = {
      downloadingAll: false,
      newDatFiles: []
    };
  }

  handleGetUserFromConnector() {
    this.clearSelectedList();
  }

  localDownloadAllAttachments = async () => {
    try {
      const { newDatFiles } = this.state;
      if (newDatFiles && newDatFiles.length > 0) {
        const zip = new JSZip();

        for (const attachment of newDatFiles) {
          const byteCharacters = atob(attachment.content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);

          const fileName = attachment.fileName ||
            `documento.${attachment.contentType ? attachment.contentType.split('/').pop() : 'dat'}`;

          zip.file(fileName, byteArray);
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, 'adjuntos_extraidos_winmailDat.zip');
      }
    } catch (error) {
      console.error('Error local downloading attachments:', error);
    }
  };


  downloadAllAttachments = () => {
    if (this.state.downloadingAll) {
      return;
    }

    if (this.state.newDatFiles.length > 0) {
      this.localDownloadAllAttachments();
    }

    const message = this.props.selectedMessage;
    const attachments = message.attachments
      ? message.attachments.filter(
          a => !a.contentId || (a.contentId && a.contentType)
        )
      : [];

    if (attachments.length === 0) {
      return;
    }

    // Use the first attachment to construct the download-all URL
    const firstAttachment = attachments[0];
    const splitHref = firstAttachment._links.download.href.split('/');
    const replaceHref = firstAttachment._links.download.href.replace(
      splitHref[10],
      'download-all'
    );

    const downloadAllAttachment = {
      attachment: firstAttachment,
      _links: {
        download: {
          href: replaceHref
        }
      }
    };

    this.setState({
      downloadingAll: true
    });

    const ieCompatibleFinally = () =>
      this.setState({
        downloadingAll: false
      });

    downloadAttachmentAll(this.props.credentials, downloadAllAttachment)
      .then(ieCompatibleFinally)
      .catch(ieCompatibleFinally);
  };
  

  render() {
    const folder = this.props.currentFolder;
    const message = this.props.selectedMessage;
    const showPreview = this.props.showPreview;
    const { newDatFiles } = this.state;

    const firstFrom = addressGroups(
      message.from && message.from.length > 0 ? message.from[0] : ''
    );

    let attachments = message.attachments
      ? message.attachments.filter(
          a => !a.contentId || (a.contentId && a.contentType)
        )
      : [];

    if (newDatFiles.length > 0) {
      attachments = attachments.concat(newDatFiles);
    }

    return (
      <>
        <div
          className={`
            messageViewer 
            ${!showPreview ? 'messageViewer-p' : 'split-messageViewer-p'}`
          }
          id="message-viewer-container">
          <Spinner
            visible={
              (this.props.refreshMessageActiveRequests > 0 &&
                !message.content) ||
              this.state.downloadingAll
            }
          />
          <div className="header mb-4">
            <div className="name-date">
              <span className="inter-semibold-16 text-color-grey-800">
                {firstFrom.name !== firstFrom.email ?
                 `${firstFrom.name} <${firstFrom.email}>` : firstFrom.email}
              </span>
              <span className="date">
                {moment(message.receivedDate).format('DD/MM/YYYY · HH:mm')}
              </span>
            </div>
            <div className="subject-icon-wrapper">
              <h1 className="subject mt-1" title={this.props.selectedMessage.subject}>
                <div className="title">{this.props.selectedMessage.subject}</div>
                {this.props.isFullMessageContent && (<div
                  className="folder"
                  onClick={() => this.onFolderClick(folder)}>
                  <div>{folder.name}</div>
                </div>)}
              </h1>
              <div className="d-flex">
                {message.flagged &&
                  <span
                    id={`favorite-${message.uid}`}
                    className="ml-2"
                  >
                    <FavoriteIcon />
                  </span>
                }
                {message.important && (
                  <span
                    title={i18n.t("message-header.important")}
                    id={`important-${message.uid}`}
                    className="ml-2"
                  >
                    <TagIcon />
                  </span>
                )}
                {message.hasAttachments && (
                  <span
                    title={i18n.t("message-header.attachment")}
                    id={`attachment-${message.uid}`}
                    className="ml-2"
                  >
                    <ClipIcon />
                  </span>
                )}
              </div>
            </div>
            <div className="fromDate">
              <div className="from">
                <HeaderTo recipients={message.recipients} />
              </div>
            </div>
          </div>
          <div className="message-content-wrapper">
            {attachments && attachments.length > 0 && (
              <div className="attachment-wrapper">
                <div className="attachments-container">
                  {attachments.map((a, index) => (
                      <AttachmentCard
                        i={index}
                        key={index}
                        attachment={a}
                        className="attachment-card"
                        variant="viewer"
                      />
                  ))}
                </div>
                <div className="download-all-attachments-container">
                  <div
                    className={`download-all-button`}
                    onClick={this.downloadAllAttachments}
                    title={i18n.t('general.download_all')}
                  >
                    <img
                      src={`/assets/images/download.svg`}
                      alt={i18n.t('general.download_all')}
                      width="17px"
                      height="17px"
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="divider-message" />
            <div
              className={`message-content ${
                attachments && attachments.length > 0
                  ? 'with-attachments'
                  : 'no-attachments'
              }`}
            >
              <div className="message-body">
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitize.sanitize(message.content)
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        {message && message.readConfirmation && !message.seen &&
        <ToastComponent
          ref={toast => {
            this.toastObj = toast;
          }}
          id="toast_pos"
          position={{ X: 'Right', Y: 'Top' }}
          animation={{
            hide: { duration: '1' },
            show: { duration: '200' }
          }}
          timeOut={7000}
          showCloseButton
        />
        }
      </>
    );
  }

  sendMessagesClassifications = event => {
    const findSelectedMessage = this.props.selectedMessagesClassification.find(
      x => x.id === event.detail.id
    );
    const newSelectedMessage = {
      ...findSelectedMessage,
      idActuation: event.detail.idActuation
    };
    this.props.addMessageClassification([{ ...newSelectedMessage }]);
  };

  componentDidMount() {
    if (this.props.isFullMessageContent) {
      this.clearSelectedList();
      window.addEventListener('GetUserFromLexonConnector', this.handleGetUserFromConnector);
      window.addEventListener('GetUserFromCentinelaConnector', this.handleGetUserFromConnector);
    }

    window.addEventListener('SendMessagesClassifications', this.sendMessagesClassifications);

    const message = this.props.selectedMessage;

    if (message && message?.attachments) {
      this.onDownloadWinMailDatFiles(message.attachments);
    }

    setTimeout(() => {
      if (message && message.readConfirmation && !message.seen) {
        this.toastObj.show({
          content: i18n.t('toasts.readConfirmationSent'),
          cssClass: 'e-toast-success',
          icon: 'lf-icon lf-icon-check-round'
        });
      }
    }, 500);
  }

  componentDidUpdate(prevProps) {
    const { sidebarLexonDocked, showPreview } = this.props;
    if (this.props.selectedMessage !== prevProps.selectedMessage &&
        this.props.selectedMessage?.attachments) {
      this.onDownloadWinMailDatFiles(this.props.selectedMessage.attachments);
    }
    if (prevProps.sidebarLexonDocked !== sidebarLexonDocked && !showPreview) { 
      this.clearSelectedList();
    }
  }

  async componentWillUnmount() {
    const { lexon, folders, messageCache } = this.props;

    clearTimeout(this.refreshPollTimeout);

    window.removeEventListener(
      'GetUserFromLexonConnector',
      this.handleGetUserFromLexonConnector
    );
    window.removeEventListener('SendMessagesClassifications', this.sendMessagesClassifications);

    if (lexon.idCaseFile !== null && lexon.idCaseFile !== undefined) {
      window.dispatchEvent(new CustomEvent('RemoveCaseFile'));
      this.props.setCaseFile({
        casefile: null,
        bbdd: null,
        idCompany: null
      });
    }

    if (
      lexon.idEmail &&
      lexon.idEmail !== null &&
      lexon.idEmail !== undefined
    ) {
      this.props.resetIdEmail(); // Se borra la información del email para que no vuelva a entrar si se refresca la ventana.
    }

    if (this.props.isFullMessageContent) {
      // Clear selection of currently opened message
      this.props.setSelected(
        [this.props.selectedMessage],
        false,
        this.props.currentFolder.fullName
      );

      if (this.oldSelectedList && this.oldSelectedList.length > 0) {
        const ms = this.oldSelectedList.map(item => ({
          ...item,
          messageId: item.id
        }));

        this.props.setSelected(ms, true, this.props.currentFolder.fullName);

        window.dispatchEvent(new CustomEvent('ResetList'));
        const prs = [];
        for (let i = 0; i < this.oldSelectedList.length; i++) {
          if (this.oldSelectedList[i].id && this.oldSelectedList[i].folder !== '') {
            let msgId = this.oldSelectedList[i].id;
            if (msgId) {
              msgId.replace("\t", "").replace("\n", "");
            } else {
              msgId = `${this.oldSelectedList[i].subject}||-||${this.oldSelectedList[i].receivedDate}`;
            }

            window.dispatchEvent(new CustomEvent('LoadingMessage', {detail: msgId}));

            const folder = Object.values(folders.explodedItems)
              .find(f => f.fullName === this.oldSelectedList[i].folder);

            const messages = Array.from(Object.values(messageCache[folder.folderId] || {}));
            let message = messages.find(msg => msg.messageId === this.oldSelectedList[i].id);
            if (message === null || typeof message === "undefined") {
              message = messages.find(msg =>
                msg.subject === this.oldSelectedList[i].subject &&
              msg.receivedDate === this.oldSelectedList[i].sentDateTime
              );
            }

            if (message) {
              prs.push(readMessageRaw(
                this.props.dispatch,
                this.props.credentials,
                null,
                folder,
                message
              ));
            }
          }
        }
        
        try {
          const msgs = await Promise.all(prs);
          
          for (let i = 0; i < msgs.length; i++) {
            const msg = msgs[i];

            let msgId2 = msg.message.messageId;
            if (msgId2) {
              msgId2.replace("\t", "").replace("\n", "");
            } else {
              msgId2 = `${msg.message.subject}||-||${msg.message.receivedDate}`;
            }

            window.dispatchEvent(
              new CustomEvent('Checkclick', {
                detail: {
                  uid: msg.message.uid,
                  id: msgId2,
                  extMessageId: msgId2,
                  subject: msg.message.subject,
                  sentDateTime: msg.message.receivedDate,
                  chkselected: true,
                  account: this.props.all.login.formValues.user,
                  provider: 'IMAP',
                  raw: msg.raw
                }
              })
            );
            msg.raw = null;
          }
          window.dispatchEvent(new CustomEvent('LoadedMessage'));
        } catch (error) {
          console.error('Error loading messages on unmount:', error);
        }
      }
    }
  }

  async clearSelectedList() {
    window.dispatchEvent(new CustomEvent('ResetList'));
    this.oldSelectedList = Object.assign([], this.props.selectedMessages);
    this.props.setSelected(
      this.props.selectedMessages.map(item => ({
        ...item,
        messageId: item.id
      })),
      false,
      this.props.currentFolder.fullName
    );
    window.dispatchEvent(
      new CustomEvent('CheckAllclick', {
        detail: {
          listMessages: this.oldSelectedList,
          chkselected: false
        }
      })
    );

    this.props.setSelected(
      [this.props.selectedMessage],
      true,
      this.props.currentFolder.fullName
    );

    let msgId = this.props.selectedMessage.messageId;
    if (msgId) {
      msgId = msgId.replace("\t", "").replace("\n", "");
    } else {
      msgId = `${this.props.selectedMessage.subject}||-||${this.props.selectedMessage.receivedDate}`;
    }

    window.dispatchEvent(new CustomEvent('LoadingMessage', {detail: msgId}));
    try {
      const response = await readMessageRaw(
        this.props.dispatch,
        this.props.credentials,
        null,
        this.props.currentFolder,
        this.props.selectedMessage
      );
      
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('Checkclick', {
            detail: {
              uid: this.props.selectedMessage.uid,
              id: msgId,
              extMessageId: msgId,
              subject: this.props.selectedMessage.subject,
              sentDateTime: this.props.selectedMessage.receivedDate,
              chkselected: true,
              folder: this.props.currentFolder.fullName,
              account: this.props.login.formValues.user,
              provider: 'IMAP',
              raw: response.raw,
              newDatFiles: this.state.newDatFiles?.map(file => ({
                ContentType: file.contentType,
                Filename: file.fileName || `documento.${
                  file.contentType ?
                    file.contentType.split('/').pop() : 'dat'}`,
                Content: file.content,
                checked: true
              })) || []
            }
        })
      );
      window.dispatchEvent(new CustomEvent('LoadedMessage'));
    }, 1000);

    } catch (error) {
      console.error('Error reading message:', error);
    }
  }

  async onDownloadWinMailDatFiles(attachments) {
    const { lexon, credentials } = this.props;

    const winMailDatFilesFilter = attachments?.filter(
      a => a.fileName && a.fileName.toLowerCase().endsWith('.dat')
    );
    if (winMailDatFilesFilter?.length > 0) {
      this.setState({ downloadingAll: true });
      // Call the service to download the .dat files
      for (let i = 0; i < winMailDatFilesFilter.length; i++) {
        const files = await downloadWinmailDatFiles(
          lexon.lexonToken,
          credentials,
          winMailDatFilesFilter[i]
        );
        if (files && files.length > 0) {
          this.setState({newDatFiles: files}, () => {
            this.clearSelectedList();
          });
        }
      }
      this.setState({ downloadingAll: false });
      this.clearSelectedList();
    }
  }

  onFolderClick(folder) {
    this.props.showFolder(folder);
  }
}

MessageViewer.propTypes = {
  refreshMessageActiveRequests: PropTypes.number,
  selectedMessage: PropTypes.object,
  className: PropTypes.string
};

MessageViewer.defaultProps = {
  className: ''
};

const mapStateToProps = state => ({
  refreshMessageActiveRequests: state.application.refreshMessageActiveRequests,
  currentFolder: getSelectedFolder(state) || {},
  selectedFolderId: state.application.selectedFolderId,
  selectedMessage: state.application.selectedMessage,
  selectedMessages: state.messages.selectedMessages,
  selectedMessagesClassification: state.messages.selectedMessages,
  selected: state.messages.selected,
  messageCache: state.messages.cache,
  lexon: state.lexon,
  login: state.login,
  credentials: state.application.user.credentials,
  folders: state.folders,
  all: state,
  showPreview: state.application.showPreview,
  isFullMessageContent: state.messages.isFullMessageContent,
  sidebarLexonDocked: state.messages.sidebarLexonDocked,
});

const mapDispatchToProps = dispatch => ({
  dispatch,
  showFolder: folder => {
    clearSelectedMessage(dispatch);
    dispatch(selectFolder(folder));
  },
  setCaseFile: casefile => dispatch(ACTIONS.setCaseFile(casefile)),
  resetIdEmail: () => dispatch(ACTIONS.resetIdEmail()),
  setSelected: (messages, selected, shiftKey) =>
    dispatch(setSelected(messages, selected, shiftKey)),
  addMessageClassification: messages =>
    dispatch(addMessageClassification(messages))
});

export default connect(mapStateToProps, mapDispatchToProps)(MessageViewer);
